import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  Button,
  EmptyState,
  ErrorNote,
  Loading,
  compactInputClass,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import {
  distributionApi,
  type DistributionReport,
  type ReportRow,
  type TeamMember,
} from '../lib/distribution';
import { formatDate, formatDateTime } from '../lib/format';
import { listPipelines } from '../lib/resources';

type Tab = 'equipe' | 'relatorio';

/**
 * Equipe (gestor): quem está "Disponível" para receber leads, ausências
 * (falta, férias) e a fila de leads esperando vendedor; e o relatório da
 * distribuição, com dois períodos lado a lado para comparar antes e depois.
 */
export function TeamPage({ mobile = false }: { mobile?: boolean }) {
  const [tab, setTab] = useState<Tab>('equipe');
  const tabs = (
    <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm">
      {(
        [
          ['equipe', 'Disponibilidade'],
          ['relatorio', 'Relatório'],
        ] as const
      ).map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => setTab(key)}
          className={`flex-1 rounded-md px-3 py-1 ${tab === key ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
  const body = tab === 'equipe' ? <Availability /> : <Report />;

  // App do celular (/m/equipe): cabeçalho próprio, sem o menu do CRM.
  if (mobile)
    return (
      <div className="flex min-h-screen flex-col bg-slate-50 text-slate-900">
        <header
          className="sticky top-0 z-20 space-y-2.5 border-b border-slate-200 bg-white px-4 pb-3"
          style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}
        >
          <div className="flex items-center gap-2">
            <Link
              to="/m"
              className="-ml-2 rounded-lg p-2 text-slate-500 hover:bg-slate-100"
              aria-label="Voltar para Meus leads"
              title="Voltar para Meus leads"
            >
              <ArrowLeft size={18} />
            </Link>
            <h1 className="text-lg font-semibold">Equipe</h1>
          </div>
          {tabs}
        </header>
        <main className="flex-1 p-3">{body}</main>
      </div>
    );

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Equipe</h1>
          <p className="text-sm text-slate-500">
            Distribuição de leads por disponibilidade: só recebe quem está "Disponível".
          </p>
        </div>
        {tabs}
      </header>
      {body}
    </div>
  );
}

// ------------------------------------------------------------ disponibilidade

function Availability() {
  const queryClient = useQueryClient();
  const team = useQuery({
    queryKey: ['distribution', 'team'],
    queryFn: distributionApi.team,
    refetchInterval: 20_000,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['distribution'] });
  const toggle = useMutation({
    mutationFn: ({ userId, available }: { userId: string; available: boolean }) =>
      distributionApi.setMember(userId, available),
    onSuccess: refresh,
  });

  if (team.isLoading) return <Loading />;
  if (team.isError)
    return <ErrorNote>{errorMessage(team.error, 'Falha ao carregar')}</ErrorNote>;
  const { members, queue } = team.data!;
  const online = members.filter((member) => member.available).length;

  return (
    <div className="space-y-6">
      {toggle.error && (
        <ErrorNote>{errorMessage(toggle.error, 'Não foi possível mudar')}</ErrorNote>
      )}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-slate-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-800">Pessoas</h2>
          <span className="text-xs text-slate-500">
            {online} disponível(is) agora · {queue.length} lead(s) na fila
          </span>
        </div>
        <table className="hidden w-full text-sm md:table">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Pessoa</th>
              <th className="px-4 py-2 font-medium">Recebendo leads</th>
              <th className="px-4 py-2 text-right font-medium">Hoje</th>
              <th className="px-4 py-2 text-right font-medium">Sem contato</th>
              <th className="px-4 py-2 font-medium">Ausência</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <MemberRow
                key={member.userId}
                member={member}
                busy={toggle.isPending}
                onToggle={(available) => toggle.mutate({ userId: member.userId, available })}
                onChanged={refresh}
              />
            ))}
          </tbody>
        </table>
        {/* celular: um cartão por pessoa */}
        <ul className="divide-y divide-slate-100 md:hidden">
          {members.map((member) => (
            <MemberCard
              key={member.userId}
              member={member}
              busy={toggle.isPending}
              onToggle={(available) => toggle.mutate({ userId: member.userId, available })}
              onChanged={refresh}
            />
          ))}
        </ul>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-800">Fila de distribuição</h2>
          <p className="text-xs text-slate-500">
            Leads esperando alguém ligar "Disponível". Saem em ordem de chegada, no máximo o
            limite de leads sem contato por vendedor.
          </p>
        </div>
        {queue.length === 0 ? (
          <EmptyState title="Fila vazia" hint="Todo lead que chegou já tem vendedor." />
        ) : (
          <ol className="divide-y divide-slate-100 text-sm">
            {queue.map((item, index) => (
              <li key={item.id} className="flex items-center gap-3 px-4 py-2.5">
                <span className="w-5 shrink-0 text-right text-xs tabular-nums text-slate-400">
                  {index + 1}º
                </span>
                <Link
                  to={`/negocios/${item.businessId}`}
                  className="min-w-0 flex-1 hover:underline"
                >
                  <span className="block truncate font-medium text-slate-800">
                    {item.leadName}
                  </span>
                  <span className="block truncate text-xs text-slate-400">
                    #{item.code} · {item.pipelineName} · chegou {formatDateTime(item.queuedAt)}
                  </span>
                </Link>
                <span
                  className={`shrink-0 rounded px-1.5 py-0.5 text-xs tabular-nums ${waitTone(item.queuedAt)}`}
                  title="Tempo esperando na fila"
                >
                  {minutes(waitedMinutes(item.queuedAt))}
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

const REASONS = ['Falta', 'Férias', 'Atestado', 'Folga'];

type MemberProps = {
  member: TeamMember;
  busy: boolean;
  onToggle: (available: boolean) => void;
  onChanged: () => void;
};

function MemberRow({ member, busy, onToggle, onChanged }: MemberProps) {
  return (
    <tr className="border-t border-slate-100 align-middle">
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Avatar name={member.name} size="xs" />
          <div className="min-w-0">
            <p className="truncate font-medium text-slate-800">{member.name}</p>
            <p className="truncate text-xs text-slate-400">{member.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5">
        <MemberSwitch member={member} busy={busy} onToggle={onToggle} />
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">{member.today}</td>
      <td
        className={`px-4 py-2.5 text-right tabular-nums ${member.pending ? 'font-medium text-amber-600' : ''}`}
      >
        {member.pending}
      </td>
      <td className="px-4 py-2.5">
        <AbsenceControl member={member} onChanged={onChanged} />
      </td>
    </tr>
  );
}

/** Celular: a mesma pessoa em cartão (nome, chave, números e ausência). */
function MemberCard({ member, busy, onToggle, onChanged }: MemberProps) {
  return (
    <li className="space-y-2 px-4 py-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Avatar name={member.name} size="xs" />
          <p className="truncate font-medium text-slate-800">{member.name}</p>
        </div>
        <MemberSwitch member={member} busy={busy} onToggle={onToggle} />
      </div>
      <p className="text-xs text-slate-500">
        Hoje: <span className="font-medium tabular-nums text-slate-700">{member.today}</span> ·
        Sem contato:{' '}
        <span
          className={`font-medium tabular-nums ${member.pending ? 'text-amber-600' : 'text-slate-700'}`}
        >
          {member.pending}
        </span>
      </p>
      <AbsenceControl member={member} onChanged={onChanged} />
    </li>
  );
}

function MemberSwitch({
  member,
  busy,
  onToggle,
}: Pick<MemberProps, 'member' | 'busy' | 'onToggle'>) {
  const absent = Boolean(member.absentUntil);
  return (
    <div className="shrink-0">
      <button
        type="button"
        role="switch"
        aria-checked={member.available}
        aria-label={`${member.name}: recebendo leads`}
        disabled={absent || busy}
        onClick={() => onToggle(!member.available)}
        className="flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
        title={absent ? 'Tire a ausência para ligar' : undefined}
      >
        <span
          className={`relative h-5 w-9 shrink-0 rounded-full transition ${member.available ? 'bg-emerald-500' : 'bg-slate-300'}`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
              member.available ? 'left-[18px]' : 'left-0.5'
            }`}
          />
        </span>
        <span className="text-xs text-slate-600">
          {member.absentUntil ? 'Ausente' : member.available ? 'Disponível' : 'Indisponível'}
        </span>
      </button>
      {member.changedAt && (
        <p className="mt-0.5 text-[11px] text-slate-400">
          desde {formatDateTime(member.changedAt)}
        </p>
      )}
    </div>
  );
}

function AbsenceControl({ member, onChanged }: Pick<MemberProps, 'member' | 'onChanged'>) {
  const [editing, setEditing] = useState(false);
  const [until, setUntil] = useState(today());
  const [reason, setReason] = useState(REASONS[0]);
  const absence = useMutation({
    mutationFn: (value: string | null) =>
      distributionApi.setAbsence(member.userId, value, value ? reason : undefined),
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
  });

  if (member.absentUntil)
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">
          {member.absentReason ?? 'Ausente'} até {formatDate(member.absentUntil)}
        </span>
        <button
          type="button"
          className="text-slate-500 underline hover:text-slate-800"
          disabled={absence.isPending}
          onClick={() => absence.mutate(null)}
        >
          Tirar
        </button>
      </div>
    );

  if (editing)
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <select
          className={compactInputClass}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        >
          {REASONS.map((item) => (
            <option key={item}>{item}</option>
          ))}
        </select>
        <span className="text-xs text-slate-500">até</span>
        <input
          type="date"
          className={compactInputClass}
          value={until}
          min={today()}
          onChange={(event) => setUntil(event.target.value)}
        />
        <Button
          variant="primary"
          disabled={!until || absence.isPending}
          onClick={() => absence.mutate(until)}
        >
          Salvar
        </Button>
        <Button variant="ghost" onClick={() => setEditing(false)}>
          Cancelar
        </Button>
        {absence.error && (
          <span className="w-full text-xs text-red-600">
            {errorMessage(absence.error, 'Falha ao salvar')}
          </span>
        )}
      </div>
    );

  return (
    <button
      type="button"
      className="text-xs text-slate-500 underline hover:text-slate-800"
      onClick={() => setEditing(true)}
    >
      Marcar ausência
    </button>
  );
}

/** Minutos desde que o lead entrou na fila. */
function waitedMinutes(queuedAt: string): number {
  return Math.max(0, (Date.now() - new Date(queuedAt).getTime()) / 60_000);
}

/** Cor da espera: até 10 min neutro, até 25 min amarelo, depois vermelho. */
function waitTone(queuedAt: string): string {
  const waited = waitedMinutes(queuedAt);
  if (waited < 10) return 'bg-slate-100 text-slate-600';
  if (waited < 25) return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-700';
}

// ------------------------------------------------------------------ relatório

interface Period {
  from: string;
  to: string;
}

function Report() {
  const pipelines = useQuery({ queryKey: ['pipelines', 'all'], queryFn: listPipelines });
  const list = pipelines.data?.data ?? [];
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const current =
    list.find((item) => item.id === pipelineId) ??
    list.find((item) => /comercial/i.test(item.name)) ??
    list[0];
  const [periodB, setPeriodB] = useState<Period>({ from: daysAgo(6), to: today() });
  const [periodA, setPeriodA] = useState<Period>({ from: daysAgo(13), to: daysAgo(7) });
  const [sla, setSla] = useState(25);

  const fetchPeriod = (period: Period) =>
    distributionApi.report({ pipelineId: current!.id, ...period, sla });
  const a = useQuery({
    queryKey: ['distribution', 'report', current?.id, periodA, sla],
    queryFn: () => fetchPeriod(periodA),
    enabled: Boolean(current),
  });
  const b = useQuery({
    queryKey: ['distribution', 'report', current?.id, periodB, sla],
    queryFn: () => fetchPeriod(periodB),
    enabled: Boolean(current),
  });

  if (pipelines.isLoading) return <Loading />;
  if (!current) return <EmptyState title="Nenhuma pipeline" />;

  return (
    <div className="space-y-4">
      <section className="flex flex-wrap items-end gap-x-4 gap-y-3 rounded-xl border border-slate-200 bg-white p-3 text-sm sm:p-4">
        <label className="space-y-1">
          <span className="block text-xs text-slate-500">Pipeline</span>
          <select
            className={compactInputClass}
            value={current.id}
            onChange={(event) => setPipelineId(event.target.value)}
          >
            {list.map((pipeline) => (
              <option key={pipeline.id} value={pipeline.id}>
                {pipeline.name}
              </option>
            ))}
          </select>
        </label>
        <PeriodPicker label="Período A (antes)" value={periodA} onChange={setPeriodA} />
        <PeriodPicker label="Período B (depois)" value={periodB} onChange={setPeriodB} />
        <label className="space-y-1">
          <span className="block text-xs text-slate-500">Prazo (min)</span>
          <input
            type="number"
            min={1}
            className={`${compactInputClass} w-20`}
            value={sla}
            onChange={(event) => setSla(Math.max(1, Number(event.target.value) || 25))}
          />
        </label>
      </section>

      {a.isLoading || b.isLoading ? (
        <Loading />
      ) : a.isError || b.isError ? (
        <ErrorNote>
          {errorMessage(a.error ?? b.error, 'Falha ao carregar o relatório')}
        </ErrorNote>
      ) : (
        <Comparison a={a.data!} b={b.data!} />
      )}
    </div>
  );
}

function PeriodPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Period;
  onChange: (period: Period) => void;
}) {
  return (
    <div className="space-y-1">
      <span className="block text-xs text-slate-500">{label}</span>
      <div className="flex items-center gap-1">
        <input
          type="date"
          className={compactInputClass}
          value={value.from}
          max={value.to}
          onChange={(event) => onChange({ ...value, from: event.target.value })}
        />
        <span className="text-xs text-slate-400">a</span>
        <input
          type="date"
          className={compactInputClass}
          value={value.to}
          min={value.from}
          onChange={(event) => onChange({ ...value, to: event.target.value })}
        />
      </div>
    </div>
  );
}

type Metric = {
  label: string;
  value: (row: Omit<ReportRow, 'attendantId' | 'name'>) => number | null;
  format: (value: number | null) => string;
  /** maior é melhor? (decide a cor da mudança) */
  higherIsBetter: boolean;
};

const percentWithin = (row: Omit<ReportRow, 'attendantId' | 'name'>) =>
  row.received ? (row.withinSla / row.received) * 100 : null;

function Comparison({ a, b }: { a: DistributionReport; b: DistributionReport }) {
  const metrics: Metric[] = [
    { label: 'Recebidos', value: (row) => row.received, format: plain, higherIsBetter: true },
    {
      label: `% contatado em até ${b.slaMinutes} min`,
      value: percentWithin,
      format: (value) => (value === null ? '—' : `${Math.round(value)}%`),
      higherIsBetter: true,
    },
    {
      label: 'Tempo médio até o 1º contato',
      value: (row) => row.avgMinutes,
      format: minutes,
      higherIsBetter: false,
    },
    {
      label: 'Sem nenhum contato',
      value: (row) => row.received - row.contacted,
      format: plain,
      higherIsBetter: false,
    },
    {
      label: 'Prazos perdidos',
      value: (row) => row.expired,
      format: plain,
      higherIsBetter: false,
    },
    {
      label: 'Horas disponível',
      value: (row) => row.availableHours,
      format: (value) => (value === null ? '—' : `${value}h`),
      higherIsBetter: true,
    },
  ];

  const people = useMemo(() => {
    const names = new Map<string, string>();
    for (const row of [...a.rows, ...b.rows]) names.set(row.attendantId ?? '', row.name);
    return [...names.entries()].sort((x, y) => x[1].localeCompare(y[1]));
  }, [a, b]);
  const rowOf = (report: DistributionReport, id: string) =>
    report.rows.find((row) => (row.attendantId ?? '') === id);

  return (
    <div className="space-y-3">
      {(!a.totals.hasWhatsappClicks || !b.totals.hasWhatsappClicks) && (
        <p className="rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800">
          O clique no WhatsApp só começou a ser registrado com o piloto. Em período sem clique
          registrado, só a mudança de etapa conta como contato — o tempo de antes pode parecer
          maior do que foi.
        </p>
      )}
      {/* celular: um cartão por vendedor, cada número com A → B */}
      <ul className="space-y-2 md:hidden">
        {[
          ...people.map(([id, name]) => ({ id, name, a: rowOf(a, id), b: rowOf(b, id) })),
          { id: 'total', name: 'Total', a: a.totals, b: b.totals },
        ].map((person) => (
          <li
            key={person.id}
            className={`rounded-xl border border-slate-200 p-3 text-sm ${person.id === 'total' ? 'bg-slate-50' : 'bg-white'}`}
          >
            <p className="mb-1.5 font-semibold text-slate-800">{person.name}</p>
            <dl className="space-y-1">
              {metrics.map((metric) => (
                <div key={metric.label} className="flex items-baseline justify-between gap-3">
                  <dt className="text-xs text-slate-500">{metric.label}</dt>
                  <dd className="shrink-0">
                    <Change metric={metric} a={person.a} b={person.b} />
                  </dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white md:block">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Vendedor</th>
              {metrics.map((metric) => (
                <th key={metric.label} className="px-3 py-2 text-right font-medium">
                  {metric.label}
                  <span className="block font-normal text-slate-400">A → B</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {people.map(([id, name]) => (
              <tr key={id} className="border-t border-slate-100">
                <td className="px-4 py-2 font-medium text-slate-800">{name}</td>
                {metrics.map((metric) => (
                  <Cell key={metric.label} metric={metric} a={rowOf(a, id)} b={rowOf(b, id)} />
                ))}
              </tr>
            ))}
            <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
              <td className="px-4 py-2 text-slate-800">Total</td>
              {metrics.map((metric) => (
                <Cell key={metric.label} metric={metric} a={a.totals} b={b.totals} />
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">
        Espera média na fila (só no piloto): A {minutes(a.totals.avgQueueMinutes)} · B{' '}
        {minutes(b.totals.avgQueueMinutes)}. O tempo conta da chegada do lead até o 1º contato
        (clique no WhatsApp ou mudança de etapa).
      </p>
    </div>
  );
}

type ChangeProps = {
  metric: Metric;
  a?: Omit<ReportRow, 'attendantId' | 'name'>;
  b?: Omit<ReportRow, 'attendantId' | 'name'>;
};

function Cell(props: ChangeProps) {
  return (
    <td className="px-3 py-2 text-right tabular-nums">
      <Change {...props} />
    </td>
  );
}

/** "antes → depois", com a mudança em verde (melhorou) ou vermelho (piorou). */
function Change({ metric, a, b }: ChangeProps) {
  const before = a ? metric.value(a) : null;
  const after = b ? metric.value(b) : null;
  const better =
    before !== null && after !== null && before !== after
      ? after > before === metric.higherIsBetter
      : null;
  return (
    <span className="tabular-nums">
      <span className="text-slate-400">{metric.format(before)}</span>
      <span className="mx-1 text-slate-300">→</span>
      <span
        className={
          better === null ? 'text-slate-800' : better ? 'text-emerald-600' : 'text-red-600'
        }
      >
        {metric.format(after)}
      </span>
    </span>
  );
}

const plain = (value: number | null) => (value === null ? '—' : String(value));

function minutes(value: number | null): string {
  if (value === null) return '—';
  if (value < 60) return `${Math.round(value)} min`;
  const hours = Math.floor(value / 60);
  return `${hours}h${String(Math.round(value % 60)).padStart(2, '0')}`;
}

function today(): string {
  return daysAgo(0);
}

function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
